/**
 * Autosaved copies of open projects with unsaved changes, kept in IndexedDB (available both in
 * browsers and in the desktop webview), one per project, so unsaved work survives a crash or a
 * closed tab. The app also copies them to the account when signed in (see apps/web cloud.ts).
 */
export interface RecoveryEntry {
  /** Project id. */
  id: string;
  bytes: Uint8Array;
  /** File name, or project title when never saved. */
  name: string;
  savedAt: number;
}

const DB = 'tablo';
const STORE = 'recovery';
/** Copies kept on this device: the oldest beyond this are dropped. */
export const MAX_RECOVERIES = 20;
/** Key of the last session's record (not a project copy). */
const SESSION_KEY = '__session';

/**
 * The project open when the app was last used, reopened at the next start:
 *   - 'file': a project not kept anywhere else (new, or opened from / saved to a file): its
 *     bytes, the file it belongs to (name, and handle to save back to when the platform allows);
 *   - 'library': a project kept on this device (by its library key);
 *   - 'cloud': a cloud project (by id; it opens from the device's copy when offline).
 */
export interface SessionEntry {
  kind: 'file' | 'library' | 'cloud';
  /** Project id ('file'), library key ('library') or cloud project id ('cloud'). */
  ref: string;
  bytes?: Uint8Array;
  file?: { name: string; handle?: unknown } | null;
  /** 'file': it had changes not saved to its file. */
  unsaved?: boolean;
  savedAt: number;
}

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Run one transaction; resolves when it is done (null when storage is unavailable or it failed). */
function run<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | void,
): Promise<{ value: T | null } | null> {
  return open().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE, mode);
          const req = fn(tx.objectStore(STORE));
          let value: T | null = null;
          if (req) req.onsuccess = () => (value = (req.result as T) ?? null);
          tx.oncomplete = () => (db.close(), resolve({ value }));
          tx.onerror = () => (db.close(), resolve(null));
          tx.onabort = () => (db.close(), resolve(null));
        } catch {
          db.close();
          resolve(null);
        }
      }),
  );
}

/** Every copy on this device, newest first. */
export async function listRecoveries(): Promise<RecoveryEntry[]> {
  const out: RecoveryEntry[] = [];
  await run('readonly', (s) => {
    const req = s.openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      if (c.key === SESSION_KEY) return c.continue();
      const e = c.value as Partial<RecoveryEntry>;
      // Copies from older versions (one slot, key "current") have no id: their key stands in.
      if (e?.bytes) out.push({ ...(e as RecoveryEntry), id: e.id ?? String(c.key) });
      c.continue();
    };
  });
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/** Save the copy of a project (replacing its previous one), then drop the oldest beyond the limit. */
export async function saveRecovery(e: RecoveryEntry): Promise<void> {
  await run('readwrite', (s) => {
    s.put(e, e.id);
  });
  const all = await listRecoveries();
  const extra = all.slice(MAX_RECOVERIES).map((x) => x.id);
  if (extra.length) await run('readwrite', (s) => void extra.forEach((id) => s.delete(id)));
}

/** The newest copy, if any. */
export const loadRecovery = async (): Promise<RecoveryEntry | null> => (await listRecoveries())[0] ?? null;

/** Drop a project's copy. */
export const clearRecovery = (id: string) =>
  run('readwrite', (s) => {
    s.delete(id);
  }).then(() => undefined);

/** Remember the open project. A file handle that cannot be stored is left out (name kept). */
export async function saveSession(e: SessionEntry): Promise<void> {
  const ok = await run('readwrite', (s) => {
    s.put(e, SESSION_KEY);
  });
  if (!ok && e.file?.handle)
    await run('readwrite', (s) => {
      s.put({ ...e, file: { name: e.file!.name } }, SESSION_KEY);
    });
}
export const loadSession = async (): Promise<SessionEntry | null> =>
  (await run<SessionEntry>('readonly', (s) => s.get(SESSION_KEY) as IDBRequest<SessionEntry>))?.value ?? null;
